/**
 * Workflow Executor — Orchestrates sequential DAG execution.
 *
 * Walks the validated topological order, dispatching each step through
 * stepRunner, wiring outputs to downstream inputs via outputMapper,
 * and recording provenance. Supports cancellation via AbortController
 * and callback-based UI updates (D7).
 *
 * @module gibbs/workflow/executor
 */

import { validate } from './validation';
import { resolveInputs } from './outputMapper';
import { runStep } from './stepRunner';
import { ProvenanceLog } from './provenance';
import { ExecutionStatus } from './Workflow';
import { getAction } from '../registry/index';

// ─── Module-level AbortController ──────────────────────────────────────────

/** @type {AbortController|null} */
let activeController = null;

/**
 * Cancel the currently executing workflow.
 * No-op if no workflow is running.
 */
export function cancelWorkflow() {
    if (activeController) {
        activeController.abort();
    }
}

// ─── Execute Workflow ──────────────────────────────────────────────────────

/**
 * Execute a validated workflow DAG sequentially in topological order.
 *
 * @param {import('./Workflow.js').Workflow} workflow - Validated Workflow DAG
 * @param {Object} workspace - Current workspace state (getActiveCompound, firstCaseData, etc.)
 * @param {Object} [callbacks={}] - Lifecycle callbacks (D7)
 * @param {Function} [callbacks.onStepStart]
 * @param {Function} [callbacks.onStepComplete]
 * @param {Function} [callbacks.onStepFailed]
 * @param {Function} [callbacks.onFailureDecision]
 * @param {function(ProvenanceLog): void} [callbacks.onWorkflowComplete]
 * @param {function(number): void} [callbacks.onWorkflowCancelled]
 * @returns {Promise<{completed: boolean, provenance: ProvenanceLog, errors: string[]}>}
 */
export async function executeWorkflow(workflow, workspace, callbacks = {}) {
    const errors = [];
    const provenance = new ProvenanceLog();
    const stepResults = new Map(); // nodeId → outputs from runStep

    // ── Step 1: Validate and get topological order ─────────────────────
    const validationResult = validate(workflow);
    if (!validationResult.valid) {
        return {
            completed: false,
            provenance,
            errors: validationResult.errors,
        };
    }

    const { topologicalOrder } = validationResult;

    // ── Step 2: Create AbortController ─────────────────────────────────
    const controller = new AbortController();
    activeController = controller;

    // ── Step 3: Initialize all nodes to PENDING ────────────────────────
    for (const nodeId of topologicalOrder) {
        workflow.setExecutionState(nodeId, { status: ExecutionStatus.PENDING });
    }

    // ── Step 4: Walk topological order sequentially ────────────────────
    let completed = true;
    let cancelledAtIndex = -1;

    // Sequential DAG execution — each step depends on prior outputs and on
    // user decisions at failure points. Awaits inside the loop are required.
    /* eslint-disable no-await-in-loop */
    for (let stepIndex = 0; stepIndex < topologicalOrder.length; stepIndex++) {
        const nodeId = topologicalOrder[stepIndex];
        const node = workflow.getNode(nodeId);

        // 4a. Check cancellation before starting step
        if (controller.signal.aborted) {
            cancelledAtIndex = stepIndex;
            completed = false;
            markRemainingCancelled(workflow, topologicalOrder, stepIndex);
            break;
        }

        // Retry loop — allows re-executing a failed step when user chooses "retry"
        let stepDone = false;
        let shouldBreak = false;

        while (!stepDone) {
            // 4b. Notify step start
            callbacks.onStepStart?.(stepIndex, node);

            // 4c. Set node to RUNNING
            workflow.setExecutionState(nodeId, {
                status: ExecutionStatus.RUNNING,
                startTime: Date.now(),
            });

            // 4d. Resolve inputs from upstream outputs via outputMapper
            const { resolvedInputs, errors: inputErrors } = resolveInputs(
                node, workflow, stepResults, workspace
            );

            // 4e. If input resolution failed, mark FAILED
            if (inputErrors.length > 0) {
                const errorMsg = inputErrors.join('; ');
                workflow.setExecutionState(nodeId, {
                    status: ExecutionStatus.FAILED,
                    error: errorMsg,
                    endTime: Date.now(),
                });
                errors.push(...inputErrors);
                callbacks.onStepFailed?.(stepIndex, node, errorMsg);

                // Await user decision if callback provided
                const decision = callbacks.onFailureDecision
                    ? await callbacks.onFailureDecision(stepIndex, node, errorMsg)
                    : 'abort';

                if (decision === 'retry') {
                    continue; // Re-run this step
                } else if (decision === 'skip') {
                    workflow.setExecutionState(nodeId, { status: ExecutionStatus.SKIPPED });
                    provenance.startStep(stepIndex, nodeId, node.actionId, node.paramOverrides);
                    provenance.endStep(stepIndex, ExecutionStatus.SKIPPED, null, errorMsg);
                    stepDone = true;
                } else {
                    // abort
                    completed = false;
                    markRemainingCancelled(workflow, topologicalOrder, stepIndex + 1);
                    stepDone = true;
                    shouldBreak = true;
                }
                continue;
            }

            // 4f. Log provenance start
            provenance.startStep(stepIndex, nodeId, node.actionId, node.paramOverrides);

            // Record data lineage from incoming edges
            const incomingEdges = workflow.getIncomingEdges(nodeId);
            for (const edge of incomingEdges) {
                const sourceIndex = topologicalOrder.indexOf(edge.sourceNodeId);
                if (sourceIndex >= 0) {
                    provenance.addDataLineage(
                        stepIndex, edge.inputName, sourceIndex, edge.outputName
                    );
                }
            }

            // 4g. Execute the step
            try {
                const stepResult = await runStep(node, resolvedInputs, workspace, {
                    abortSignal: controller.signal,
                });

                if (stepResult.success) {
                    // 4h. Success path
                    workflow.setExecutionState(nodeId, {
                        status: ExecutionStatus.SUCCEEDED,
                        endTime: Date.now(),
                        resultSummary: buildResultSummary(stepResult.outputs),
                    });

                    // Store outputs for downstream resolution
                    stepResults.set(nodeId, stepResult.outputs);

                    // Log provenance completion
                    provenance.endStep(
                        stepIndex,
                        ExecutionStatus.SUCCEEDED,
                        buildResultSummary(stepResult.outputs)
                    );

                    callbacks.onStepComplete?.(stepIndex, node, stepResult);

                    // 5.7: Active compound propagation
                    propagateActiveCompound(node, stepResult.outputs, workspace);
                    stepDone = true;
                } else {
                    // 4i. Failure path
                    const errorMsg = stepResult.error || 'Unknown step error';
                    workflow.setExecutionState(nodeId, {
                        status: ExecutionStatus.FAILED,
                        error: errorMsg,
                        endTime: Date.now(),
                    });
                    errors.push(`Step "${nodeId}" (${node.actionId}) failed: ${errorMsg}`);

                    provenance.endStep(stepIndex, ExecutionStatus.FAILED, null, errorMsg);

                    callbacks.onStepFailed?.(stepIndex, node, errorMsg);

                    // Await user decision if callback provided
                    const decision = callbacks.onFailureDecision
                        ? await callbacks.onFailureDecision(stepIndex, node, errorMsg)
                        : 'abort';

                    if (decision === 'retry') {
                        continue; // Re-run this step
                    } else if (decision === 'skip') {
                        workflow.setExecutionState(nodeId, { status: ExecutionStatus.SKIPPED });
                        stepDone = true;
                    } else {
                        // abort
                        completed = false;
                        markRemainingCancelled(workflow, topologicalOrder, stepIndex + 1);
                        stepDone = true;
                        shouldBreak = true;
                    }
                }
            } catch (err) {
                // Unexpected error (timeout, abort, etc.)
                const errorMsg = err.message || String(err);

                // User-initiated cancel — mark the step CANCELLED (not FAILED)
                // and break out without prompting the failure-decision callback.
                if (controller.signal.aborted) {
                    workflow.setExecutionState(nodeId, {
                        status: ExecutionStatus.CANCELLED,
                        endTime: Date.now(),
                    });
                    provenance.endStep(stepIndex, ExecutionStatus.CANCELLED, null, errorMsg);
                    cancelledAtIndex = stepIndex;
                    completed = false;
                    markRemainingCancelled(workflow, topologicalOrder, stepIndex + 1);
                    stepDone = true;
                    shouldBreak = true;
                    continue;
                }

                workflow.setExecutionState(nodeId, {
                    status: ExecutionStatus.FAILED,
                    error: errorMsg,
                    endTime: Date.now(),
                });
                errors.push(`Step "${nodeId}" (${node.actionId}) threw: ${errorMsg}`);

                provenance.endStep(stepIndex, ExecutionStatus.FAILED, null, errorMsg);

                callbacks.onStepFailed?.(stepIndex, node, errorMsg);

                // Await user decision if callback provided
                const decision = callbacks.onFailureDecision
                    ? await callbacks.onFailureDecision(stepIndex, node, errorMsg)
                    : 'abort';

                if (decision === 'retry') {
                    continue; // Re-run this step
                } else if (decision === 'skip') {
                    workflow.setExecutionState(nodeId, { status: ExecutionStatus.SKIPPED });
                    stepDone = true;
                } else {
                    // abort
                    completed = false;
                    markRemainingCancelled(workflow, topologicalOrder, stepIndex + 1);
                    stepDone = true;
                    shouldBreak = true;
                }
            }
        }

        if (shouldBreak) break;
    }
    /* eslint-enable no-await-in-loop */

    // ── Step 5: Final callbacks ────────────────────────────────────────
    activeController = null;

    if (cancelledAtIndex >= 0) {
        callbacks.onWorkflowCancelled?.(cancelledAtIndex);
    } else {
        callbacks.onWorkflowComplete?.(provenance);
    }

    return { completed, provenance, errors };
}

// ─── Helpers ───────────────────────────────────────────────────────────────

/**
 * Mark all remaining nodes (from startIndex onward) as CANCELLED.
 *
 * @param {import('./Workflow.js').Workflow} workflow
 * @param {string[]} topologicalOrder
 * @param {number} startIndex
 */
function markRemainingCancelled(workflow, topologicalOrder, startIndex) {
    for (let i = startIndex; i < topologicalOrder.length; i++) {
        workflow.setExecutionState(topologicalOrder[i], {
            status: ExecutionStatus.CANCELLED,
        });
    }
}

/**
 * Build a reference-only result summary for provenance.
 * Extracts lightweight references (names, IDs) rather than full objects.
 *
 * @param {Object} outputs - Step outputs from runStep
 * @returns {Object|null}
 */
function buildResultSummary(outputs) {
    if (!outputs || Object.keys(outputs).length === 0) return null;

    const summary = {};
    for (const [key, value] of Object.entries(outputs)) {
        if (key === 'rawResult') {
            summary.rawResult = typeof value === 'object' ? '[object]' : String(value);
        } else if (value && typeof value === 'object') {
            // Extract lightweight references
            if (value.name) summary[key] = { name: value.name };
            else if (value.id) summary[key] = { id: value.id };
            else if (value.pdbId) summary[key] = { pdbId: value.pdbId };
            else summary[key] = '[object]';
        } else {
            summary[key] = value;
        }
    }
    return summary;
}

/**
 * After a successful step, check if any output has WorkflowType 'Compound'.
 * If so, update the workspace active compound.
 *
 * @param {import('./Workflow.js').WorkflowNode} node
 * @param {Object} outputs - Step outputs
 * @param {Object} workspace - Workspace state
 */
function propagateActiveCompound(node, outputs, workspace) {
    if (!outputs || !workspace) return;

    const actionDef = getAction(node.actionId);
    if (!actionDef) return;

    for (const outputDef of actionDef.outputs) {
        if (outputDef.type === 'Compound' && outputs[outputDef.name] != null) {
            if (typeof workspace.setActiveCompound === 'function') {
                workspace.setActiveCompound(outputs[outputDef.name]);
            }
            return; // Only propagate the first compound output
        }
    }
}
